import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MonthlyReward, monthlyWinnersApi, rankWinners, Winner } from "../api/monthlyWinners";
import { useConfirm } from "../components/ConfirmDialog";
import PageLoader from "../components/PageLoader";
import "./MonthlyWinnersPage.css";

const MONTHS = [
  "январь",
  "февраль",
  "март",
  "апрель",
  "май",
  "июнь",
  "июль",
  "август",
  "сентябрь",
  "октябрь",
  "ноябрь",
  "декабрь",
];

const MONTHS_IN = [
  "январе",
  "феврале",
  "марте",
  "апреле",
  "мае",
  "июне",
  "июле",
  "августе",
  "сентябре",
  "октябре",
  "ноябре",
  "декабре",
];

// "2026-08" → "август 2026", from the string rather than new Date().
function formatMonth(month: string, forms = MONTHS): string {
  const [year, mm] = month.split("-");
  const name = forms[Number(mm) - 1];
  return name ? `${name} ${year}` : month;
}

// The server awards the month that has just closed, so that is the one to check.
function previousMonth(): string {
  const now = new Date();
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, "0")}`;
}

type Row = Winner & { reward: MonthlyReward | null };
type GroupWinners = { id: string; name: string; rows: Row[] };

function rewardLabel(reward: MonthlyReward | null): React.ReactNode {
  if (!reward) return <span className="mav-badge mav-badge-muted">Не назначена</span>;
  if (reward.status === "given") return <span className="mav-badge mav-badge-active">Приз выдан</span>;
  if (reward.status === "selected") {
    return <span className="mav-badge mav-badge-active">{`Приз выбран: ${reward.reward_title ?? "—"}`}</span>;
  }
  return <span className="mav-badge mav-badge-dirty">Ждёт выбора приза</span>;
}

function placesSummary(winners: Winner[]): string[] {
  const byPlace = new Map<number, string[]>();
  for (const w of winners) byPlace.set(w.place, [...(byPlace.get(w.place) ?? []), w.player_name]);
  return Array.from(byPlace.entries()).map(([place, names]) => `${place} место — ${names.join(", ")}`);
}

export default function MonthlyWinnersPage() {
  const [month, setMonth] = useState(previousMonth);
  const [winners, setWinners] = useState<Winner[]>([]);
  const [rewards, setRewards] = useState<MonthlyReward[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyGroup, setBusyGroup] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, string>>({});
  const latestLoad = useRef(0);
  const { confirm, dialog } = useConfirm();

  const load = useCallback(async () => {
    const [year, mm] = month.split("-").map(Number);
    if (!year || !mm) return;
    const loadID = ++latestLoad.current;
    try {
      const [ranking, rewardList] = await Promise.all([
        monthlyWinnersApi.ranking(year, mm),
        monthlyWinnersApi.rewards(),
      ]);
      if (loadID !== latestLoad.current) return;
      setWinners(rankWinners(ranking ?? []));
      setRewards((rewardList ?? []).filter((r) => r.month.slice(0, 7) === month));
    } catch (err: any) {
      if (loadID !== latestLoad.current) return;
      console.error("Failed to load monthly winners", err);
      setError(err?.message || "Не удалось загрузить победителей");
    } finally {
      if (loadID === latestLoad.current) setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    load();
  }, [load]);

  const changeMonth = (value: string) => {
    setMonth(value);
    setResults({});
    setError(null);
  };

  const rewardByPlayer = new Map(rewards.map((r) => [r.player_id, r]));
  const groups = new Map<string, GroupWinners>();
  for (const w of winners) {
    const group = groups.get(w.group_id) ?? { id: w.group_id, name: w.group_name, rows: [] };
    group.rows.push({ ...w, reward: rewardByPlayer.get(w.player_id) ?? null });
    groups.set(w.group_id, group);
  }
  const sections = Array.from(groups.values()).sort((a, b) => a.name.localeCompare(b.name, "ru"));
  for (const s of sections) {
    s.rows.sort((a, b) => a.place - b.place || a.player_name.localeCompare(b.player_name, "ru"));
  }

  const assign = async (group: GroupWinners) => {
    const confirmed = await confirm({
      title: `Назначить награды группе «${group.name}» за ${formatMonth(month)}?`,
      body: (
        <>
          <ul className="mw-confirm-places">
            {placesSummary(group.rows).map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          <p>
            Дети получат уведомление и выберут приз в приложении. Кому награда за этот месяц уже
            назначена, тот пропускается.
          </p>
        </>
      ),
      confirmLabel: "Назначить награды",
    });
    if (!confirmed) return;
    setBusyGroup(group.id);
    setError(null);
    try {
      const result = await monthlyWinnersApi.assign(
        group.rows.map((w) => ({
          player_id: w.player_id,
          group_id: group.id,
          month: `${month}-01`,
          position: w.place,
        }))
      );
      const counts = `Назначено: ${result.assigned}, пропущено: ${result.skipped}`;
      const text =
        result.assigned === 0
          ? `${counts} — награды за ${formatMonth(month)} уже назначены.`
          : result.skipped > 0
            ? `${counts}. Пропущены дети, у которых награда за этот месяц уже есть.`
            : `${counts}.`;
      setResults((r) => ({ ...r, [group.id]: text }));
    } catch (err: any) {
      console.error("Failed to assign winners", err);
      setError(err?.message || "Не удалось назначить награды");
    } finally {
      setBusyGroup(null);
      await load();
    }
  };

  if (loading) return <PageLoader />;

  return (
    <div className="page page-wide">
      <h2>Победители месяца</h2>
      <p className="page-description">
        Тройка лучших по очкам в каждой группе, при равенстве очков место общее. Первого числа
        сервер сам назначает награды за прошедший месяц; здесь можно проверить итог и назначить
        пропущенные. Выдают призы на странице <Link to="/player-rewards">Награды игроков</Link>.
      </p>

      {error && (
        <div className="error" role="alert">
          {error}
        </div>
      )}

      <label className="cp-field mw-month">
        <span>Месяц</span>
        <input type="month" className="mav-input" value={month} onChange={(e) => changeMonth(e.target.value)} />
      </label>

      {sections.length === 0 ? (
        <div className="mav-card mw-empty">
          {error ? "—" : `В ${formatMonth(month, MONTHS_IN)} никто не набрал очков`}
        </div>
      ) : (
        sections.map((group) => (
          <section key={group.id} className="mw-group" aria-labelledby={`mw-group-${group.id}`}>
            <div className="mw-group-header">
              <h3 id={`mw-group-${group.id}`}>{group.name}</h3>
              <button
                className="mav-btn-secondary"
                disabled={busyGroup !== null}
                onClick={() => assign(group)}
              >
                Назначить награды
              </button>
            </div>
            {results[group.id] && (
              <p className="mw-result" role="status">
                {results[group.id]}
              </p>
            )}
            <div className="table-wrap">
              <table className="settings-table stack-table mw-table">
                <thead>
                  <tr>
                    <th>Место</th>
                    <th>Игрок</th>
                    <th>Очки</th>
                    <th>Награда</th>
                  </tr>
                </thead>
                <tbody>
                  {group.rows.map((row) => (
                    <tr key={row.player_id}>
                      <td className="mw-cell-place">
                        <span className={`place place-${row.place}`}>{row.place}</span>
                      </td>
                      <td className="mw-cell-player">{row.player_name}</td>
                      <td className="mw-cell-points">{row.points}</td>
                      <td className="mw-cell-reward">{rewardLabel(row.reward)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))
      )}

      {dialog}
    </div>
  );
}
